import React from 'react';

import { chromeFor } from '@shared/app-identity/appChrome';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';

const HOME_LOGO = '/icons/conway-512.png';

/**
 * Full-screen splash shown on initial application load.
 *
 * Branded per installed app: launching Conway Tasks shows the tasks tile and
 * the word "Tasks", not the generic Conway wordmark. This is the only splash the
 * desktop ever shows — the manifest's `background_color`/icon splash is an
 * Android feature, and iOS only fakes one — so without this every installed app
 * looked identical the moment it opened.
 */
const SplashScreen: React.FC = () => {
  const installedApp = useInstalledApp();
  const chrome = chromeFor(installedApp);

  return (
    <div className="bg-accent dark:bg-background text-foreground flex h-screen w-screen flex-col items-center justify-center">
      {/* Inner wrapper shifted upward for better visual balance */}
      <div className="flex -translate-y-20 flex-col items-center sm:-translate-y-28">
        {installedApp === null ? (
          // Rounded and square for the same reason as the per-app tile below:
          // the root mark is now a full-bleed maskable tile too, where it used
          // to be a transparent circular badge that needed no softening.
          <img
            src={HOME_LOGO}
            alt="Conway Logo"
            className="h-40 w-40 rounded-[22%] select-none md:h-48 md:w-48"
          />
        ) : (
          // alt="" deliberately: the app name is rendered right below it, so alt
          // text would make a screen reader announce the same thing twice.
          // Rounded because the art is a full-bleed maskable tile — square here,
          // where no launcher mask is applied to soften it.
          <img
            src={chrome.iconLarge}
            alt=""
            data-testid="app-tile"
            className="h-40 w-40 rounded-[22%] select-none md:h-48 md:w-48"
          />
        )}
        {/* The app's own name, not the product's: inside Conway Tasks the
            "Conway" is redundant — the tile is right there, and the label under
            the home-screen icon already reads just "Tasks". Kept fluid because
            the tile above it scales too. */}
        <h1 className="mt-10 text-center text-[clamp(2rem,10vw,5rem)] leading-none font-thin">
          {chrome.shortName}
        </h1>
      </div>
    </div>
  );
};

export default SplashScreen;
